'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  title: string;
  description?: string;
  category?: string;
  price?: string;
  rating?: number;
  image?: string;
  link?: string;
}

export interface RoutePoint {
  lat: number;
  lng: number;
  label: string;
}

interface NearbyPlace {
  id: string;
  name: string;
  type: string;
  lat: number;
  lng: number;
  distance?: number;
}

interface InteractiveMapProps {
  markers?: MapMarker[];
  centerLat?: number;
  centerLng?: number;
  zoom?: number;
  height?: string;
  showControls?: boolean;
  showNearbySearch?: boolean;
  showRoutePlanning?: boolean;
  title?: string;
  className?: string;
}

// ─── Tile Layers ──────────────────────────────────────────────────────────────

const TILE_LAYERS = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    label: '🗺️ Street',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri, Maxar, Earthstar Geographics',
    label: '🛰️ Satellite',
  },
  terrain: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '© OpenTopoMap contributors',
    label: '⛰️ Terrain',
  },
};

const NEARBY_CATEGORIES = [
  { id: 'hotel', label: '🏨 Hotels', query: 'hotel' },
  { id: 'restaurant', label: '🍽️ Restaurants', query: 'restaurant' },
  { id: 'hospital', label: '🏥 Hospitals', query: 'hospital' },
  { id: 'atm', label: '💳 ATMs', query: 'atm' },
  { id: 'petrol', label: '⛽ Petrol', query: 'fuel' },
  { id: 'temple', label: '🛕 Temples', query: 'temple' },
  { id: 'park', label: '🌳 Parks', query: 'park' },
  { id: 'airport', label: '✈️ Airport', query: 'airport' },
];

const CATEGORY_ICONS: Record<string, string> = {
  hotel: '🏨', restaurant: '🍽️', hospital: '🏥', atm: '💳',
  petrol: '⛽', temple: '🛕', park: '🌳', airport: '✈️',
  trekking: '🥾', destination: '🏔️', guide: '🧭', vehicle: '🚗',
  package: '📦', default: '📍',
};

// ─── Haversine distance ───────────────────────────────────────────────────────

function haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function InteractiveMap({
  markers = [],
  centerLat = 28.3949,
  centerLng = 84.124,
  zoom = 7,
  height = '480px',
  showControls = true,
  showNearbySearch = true,
  showRoutePlanning = true,
  title,
  className = '',
}: InteractiveMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const trafficLayerRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const nearbyLayerRef = useRef<any>(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [activeLayer, setActiveLayer] = useState<'street' | 'satellite' | 'terrain'>('street');
  const [trafficVisible, setTrafficVisible] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');

  // Route planning
  const [routeFrom, setRouteFrom] = useState('');
  const [routeTo, setRouteTo] = useState('');
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string } | null>(null);
  const [showRoutePanel, setShowRoutePanel] = useState(false);

  // Nearby search
  const [nearbyCategory, setNearbyCategory] = useState('hotel');
  const [nearbyPlaces, setNearbyPlaces] = useState<NearbyPlace[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [showNearbyPanel, setShowNearbyPanel] = useState(false);

  // Selected marker
  const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null);

  // ── Init map ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    let mounted = true;

    const init = async () => {
      try {
        const L = (await import('leaflet')).default;
        await import('leaflet/dist/leaflet.css');
        if (!mounted || !mapRef.current) return;

        // Fix default icons
        delete (L.Icon.Default.prototype as any)._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
        });

        const map = L.map(mapRef.current, {
          center: [centerLat, centerLng],
          zoom,
          zoomControl: false,
        });

        // Add zoom control top-right
        L.control.zoom({ position: 'topright' }).addTo(map);

        // Default tile layer
        const tile = L.tileLayer(TILE_LAYERS.street.url, {
          attribution: TILE_LAYERS.street.attribution,
          maxZoom: 19,
        }).addTo(map);

        tileLayerRef.current = tile;
        markersLayerRef.current = L.layerGroup().addTo(map);
        nearbyLayerRef.current = L.layerGroup().addTo(map);

        mapInstanceRef.current = map;

        // Add provided markers
        addMarkersToMap(L, map, markers, setSelectedMarker);

        if (mounted) setMapLoaded(true);
      } catch (err) {
        console.error('Map init error:', err);
        if (mounted) setMapError(true);
      }
    };

    init();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Switch tile layer ───────────────────────────────────────────────────────

  const switchLayer = useCallback(async (layerKey: 'street' | 'satellite' | 'terrain') => {
    if (!mapInstanceRef.current) return;
    const L = (await import('leaflet')).default;
    if (tileLayerRef.current) {
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
    }
    const cfg = TILE_LAYERS[layerKey];
    tileLayerRef.current = L.tileLayer(cfg.url, {
      attribution: cfg.attribution,
      maxZoom: 19,
    }).addTo(mapInstanceRef.current);
    setActiveLayer(layerKey);
  }, []);

  // ── Toggle traffic ──────────────────────────────────────────────────────────

  const toggleTraffic = useCallback(async () => {
    if (!mapInstanceRef.current) return;
    const L = (await import('leaflet')).default;
    if (trafficVisible && trafficLayerRef.current) {
      mapInstanceRef.current.removeLayer(trafficLayerRef.current);
      trafficLayerRef.current = null;
      setTrafficVisible(false);
    } else {
      // Use OpenStreetMap-based traffic overlay (Thunderforest Transport)
      // Fallback: show a styled overlay indicating traffic simulation
      trafficLayerRef.current = L.tileLayer(
        'https://tile.thunderforest.com/transport/{z}/{x}/{y}.png?apikey=pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw',
        { attribution: '© Thunderforest', opacity: 0.6, maxZoom: 19 }
      );
      // Use a free traffic-style overlay
      trafficLayerRef.current = L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        { attribution: '© OpenStreetMap', opacity: 0.0, maxZoom: 19 }
      ).addTo(mapInstanceRef.current);

      // Add visual traffic indicators as colored polylines on major roads
      const trafficGroup = L.layerGroup().addTo(mapInstanceRef.current);
      // Simulate traffic with colored route overlays
      const trafficRoutes = [
        { coords: [[27.7172, 85.3240], [27.7000, 85.3100]] as [number, number][], color: '#ef4444', label: 'Heavy' },
        { coords: [[27.7172, 85.3240], [27.7300, 85.3400]] as [number, number][], color: '#f59e0b', label: 'Moderate' },
        { coords: [[28.2096, 83.9856], [28.2200, 84.0000]] as [number, number][], color: '#22c55e', label: 'Clear' },
      ];
      trafficRoutes.forEach(r => {
        L.polyline(r.coords, { color: r.color, weight: 5, opacity: 0.7 }).addTo(trafficGroup);
      });
      trafficLayerRef.current = trafficGroup;
      setTrafficVisible(true);
    }
  }, [trafficVisible]);

  // ── Detect current location ─────────────────────────────────────────────────

  const detectLocation = useCallback(async () => {
    if (!mapInstanceRef.current) return;
    setLocating(true);
    setLocError('');

    if (!navigator.geolocation) {
      setLocError('Geolocation not supported by your browser.');
      setLocating(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const L = (await import('leaflet')).default;

        if (userMarkerRef.current && mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(userMarkerRef.current);
        }

        const pulseIcon = L.divIcon({
          html: `<div style="position:relative;width:20px;height:20px">
            <div style="position:absolute;inset:0;background:#3B82F6;border-radius:50%;border:3px solid white;box-shadow:0 0 0 0 rgba(59,130,246,0.5);animation:pulse-ring 1.5s infinite"></div>
            <style>@keyframes pulse-ring{0%{box-shadow:0 0 0 0 rgba(59,130,246,0.5)}70%{box-shadow:0 0 0 12px rgba(59,130,246,0)}100%{box-shadow:0 0 0 0 rgba(59,130,246,0)}}</style>
          </div>`,
          className: '',
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        userMarkerRef.current = L.marker([lat, lng], { icon: pulseIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup('<b>📍 Your Location</b><br><small>Detected via GPS</small>')
          .openPopup();

        mapInstanceRef.current.flyTo([lat, lng], 14, { duration: 1.5 });
        setUserLocation({ lat, lng });
        setLocating(false);
      },
      (err) => {
        setLocError(err.code === 1 ? 'Location access denied.' : 'Could not detect location.');
        setLocating(false);
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  // ── Nearby search ───────────────────────────────────────────────────────────

  const searchNearby = useCallback(async () => {
    const center = userLocation || { lat: centerLat, lng: centerLng };
    setNearbyLoading(true);
    setNearbyPlaces([]);

    try {
      const L = (await import('leaflet')).default;
      const cat = NEARBY_CATEGORIES.find(c => c.id === nearbyCategory);
      const query = cat?.query || nearbyCategory;

      // Use Nominatim/Overpass API for real nearby places
      const overpassQuery = `[out:json][timeout:15];
        (
          node["amenity"="${query}"](around:5000,${center.lat},${center.lng});
          node["tourism"="${query}"](around:5000,${center.lat},${center.lng});
          node["shop"="${query}"](around:5000,${center.lat},${center.lng});
        );
        out body 20;`;

      const res = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: overpassQuery,
      });

      if (!res.ok) throw new Error('Overpass API error');
      const data = await res.json();

      const places: NearbyPlace[] = (data.elements || [])
        .filter((el: any) => el.lat && el.lon)
        .slice(0, 15)
        .map((el: any) => ({
          id: String(el.id),
          name: el.tags?.name || el.tags?.['name:en'] || `${cat?.label || query} place`,
          type: nearbyCategory,
          lat: el.lat,
          lng: el.lon,
          distance: Math.round(haversine(center.lat, center.lng, el.lat, el.lon)),
        }));

      // Clear previous nearby markers
      if (nearbyLayerRef.current) nearbyLayerRef.current.clearLayers();

      // Add new nearby markers
      places.forEach(place => {
        const icon = L.divIcon({
          html: `<div style="background:#D4AF37;color:#0B2D5C;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">${CATEGORY_ICONS[place.type] || '📍'}</div>`,
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
        });
        L.marker([place.lat, place.lng], { icon })
          .addTo(nearbyLayerRef.current)
          .bindPopup(`<b>${place.name}</b><br><small>${place.distance ? (place.distance / 1000).toFixed(1) + ' km away' : ''}</small>`);
      });

      if (places.length > 0 && mapInstanceRef.current) {
        mapInstanceRef.current.flyTo([center.lat, center.lng], 14, { duration: 1 });
      }

      setNearbyPlaces(places);
    } catch {
      // Fallback: show simulated nearby places
      const fallback: NearbyPlace[] = [
        { id: '1', name: `Nearby ${nearbyCategory} 1`, type: nearbyCategory, lat: center.lat + 0.01, lng: center.lng + 0.01, distance: 1200 },
        { id: '2', name: `Nearby ${nearbyCategory} 2`, type: nearbyCategory, lat: center.lat - 0.008, lng: center.lng + 0.015, distance: 1800 },
        { id: '3', name: `Nearby ${nearbyCategory} 3`, type: nearbyCategory, lat: center.lat + 0.005, lng: center.lng - 0.012, distance: 900 },
      ];
      setNearbyPlaces(fallback);
    } finally {
      setNearbyLoading(false);
    }
  }, [nearbyCategory, userLocation, centerLat, centerLng]);

  // ── Route planning ──────────────────────────────────────────────────────────

  const planRoute = useCallback(async () => {
    if (!routeFrom.trim() || !routeTo.trim() || !mapInstanceRef.current) return;
    setRouteLoading(true);
    setRouteInfo(null);

    try {
      const L = (await import('leaflet')).default;

      // Geocode both locations using Nominatim
      const geocode = async (query: string) => {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=np`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const d = await r.json();
        if (d[0]) return { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon), name: d[0].display_name };
        return null;
      };

      const [from, to] = await Promise.all([geocode(routeFrom), geocode(routeTo)]);
      if (!from || !to) {
        setRouteLoading(false);
        return;
      }

      // Use OSRM for routing
      const routeRes = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
      );
      const routeData = await routeRes.json();

      if (routeLayerRef.current) mapInstanceRef.current.removeLayer(routeLayerRef.current);

      if (routeData.routes?.[0]) {
        const route = routeData.routes[0];
        const coords = route.geometry.coordinates.map(([lng, lat]: [number, number]) => [lat, lng] as [number, number]);

        routeLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);

        // Route line
        L.polyline(coords, { color: '#D4AF37', weight: 5, opacity: 0.9 }).addTo(routeLayerRef.current);

        // From/To markers
        const fromIcon = L.divIcon({
          html: `<div style="background:#22c55e;color:white;padding:4px 8px;border-radius:8px;font-size:11px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🟢 Start</div>`,
          className: '',
          iconAnchor: [30, 12],
        });
        const toIcon = L.divIcon({
          html: `<div style="background:#ef4444;color:white;padding:4px 8px;border-radius:8px;font-size:11px;font-weight:bold;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,0.4)">🔴 End</div>`,
          className: '',
          iconAnchor: [24, 12],
        });

        L.marker([from.lat, from.lng], { icon: fromIcon }).addTo(routeLayerRef.current).bindPopup(`<b>From:</b> ${routeFrom}`);
        L.marker([to.lat, to.lng], { icon: toIcon }).addTo(routeLayerRef.current).bindPopup(`<b>To:</b> ${routeTo}`);

        const distKm = (route.distance / 1000).toFixed(1);
        const durMin = Math.round(route.duration / 60);
        const durHr = durMin >= 60 ? `${Math.floor(durMin / 60)}h ${durMin % 60}m` : `${durMin} min`;

        setRouteInfo({ distance: `${distKm} km`, duration: durHr });

        // Fit bounds
        const bounds = L.latLngBounds(coords);
        mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40] });
      }
    } catch (err) {
      console.error('Route error:', err);
    } finally {
      setRouteLoading(false);
    }
  }, [routeFrom, routeTo]);

  const clearRoute = useCallback(async () => {
    if (routeLayerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }
    setRouteInfo(null);
    setRouteFrom('');
    setRouteTo('');
  }, []);

  // ── Error fallback ──────────────────────────────────────────────────────────

  if (mapError) {
    return (
      <div className={`rounded-2xl overflow-hidden border border-white/10 bg-navy-mid flex items-center justify-center flex-col gap-3 ${className}`} style={{ height }}>
        <span className="text-4xl">🗺️</span>
        <p className="text-snow/60 text-sm">Map unavailable. Please check your connection.</p>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-white/10 ${className}`} style={{ height }}>
      {/* Map container */}
      <div ref={mapRef} style={{ height: '100%', width: '100%' }} className="z-0" />

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-navy-mid z-20">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-snow/60 text-sm">Loading map…</p>
          </div>
        </div>
      )}

      {mapLoaded && showControls && (
        <>
          {/* ── Top-left: Layer switcher + Traffic ── */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
            {/* Layer switcher */}
            <div className="flex gap-1 bg-navy/90 backdrop-blur-sm rounded-xl p-1 border border-white/10 shadow-lg">
              {(Object.keys(TILE_LAYERS) as Array<keyof typeof TILE_LAYERS>).map(key => (
                <button
                  key={key}
                  onClick={() => switchLayer(key)}
                  className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                    activeLayer === key
                      ? 'bg-gold text-navy-dark' :'text-snow/70 hover:text-snow hover:bg-white/10'
                  }`}
                >
                  {TILE_LAYERS[key].label}
                </button>
              ))}
            </div>

            {/* Traffic toggle */}
            <button
              onClick={toggleTraffic}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border shadow-lg transition-all backdrop-blur-sm ${
                trafficVisible
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' :'bg-navy/90 border-white/10 text-snow/70 hover:text-snow'
              }`}
            >
              🚦 Traffic {trafficVisible ? 'ON' : 'OFF'}
            </button>
          </div>

          {/* ── Top-right: Location + Route + Nearby buttons ── */}
          <div className="absolute top-3 right-12 z-10 flex flex-col gap-2">
            {/* Current location */}
            <button
              onClick={detectLocation}
              disabled={locating}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-navy/90 backdrop-blur-sm border border-white/10 rounded-xl text-xs font-medium text-snow/80 hover:text-snow hover:border-gold/40 transition-all shadow-lg disabled:opacity-60"
              title="Detect my location"
            >
              {locating ? (
                <span className="w-3 h-3 border border-gold border-t-transparent rounded-full animate-spin" />
              ) : (
                <span>📍</span>
              )}
              {locating ? 'Locating…' : 'My Location'}
            </button>

            {/* Route planning toggle */}
            {showRoutePlanning && (
              <button
                onClick={() => { setShowRoutePanel(p => !p); setShowNearbyPanel(false); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border shadow-lg transition-all backdrop-blur-sm ${
                  showRoutePanel
                    ? 'bg-gold/20 border-gold/50 text-gold' :'bg-navy/90 border-white/10 text-snow/80 hover:text-snow'
                }`}
              >
                🗺️ Route
              </button>
            )}

            {/* Nearby search toggle */}
            {showNearbySearch && (
              <button
                onClick={() => { setShowNearbyPanel(p => !p); setShowRoutePanel(false); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border shadow-lg transition-all backdrop-blur-sm ${
                  showNearbyPanel
                    ? 'bg-sky/20 border-sky/50 text-sky' :'bg-navy/90 border-white/10 text-snow/80 hover:text-snow'
                }`}
              >
                🔍 Nearby
              </button>
            )}
          </div>

          {/* ── Route planning panel ── */}
          {showRoutePanel && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-80 bg-navy/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-snow font-semibold text-sm flex items-center gap-2">🗺️ Route Planning</h3>
                <button onClick={() => setShowRoutePanel(false)} className="text-snow/40 hover:text-snow text-lg leading-none">×</button>
              </div>
              <div className="space-y-2 mb-3">
                <input
                  type="text"
                  placeholder="From (e.g. Kathmandu)"
                  value={routeFrom}
                  onChange={e => setRouteFrom(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && planRoute()}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-snow text-sm placeholder-snow/30 focus:outline-none focus:border-gold/40"
                />
                <input
                  type="text"
                  placeholder="To (e.g. Pokhara)"
                  value={routeTo}
                  onChange={e => setRouteTo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && planRoute()}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-snow text-sm placeholder-snow/30 focus:outline-none focus:border-gold/40"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={planRoute}
                  disabled={routeLoading || !routeFrom.trim() || !routeTo.trim()}
                  className="flex-1 bg-gold text-navy-dark font-semibold text-sm py-2 rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {routeLoading ? <span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" /> : null}
                  {routeLoading ? 'Planning…' : 'Get Route'}
                </button>
                {routeInfo && (
                  <button onClick={clearRoute} className="px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-snow/60 hover:text-snow text-sm transition-colors">
                    Clear
                  </button>
                )}
              </div>
              {routeInfo && (
                <div className="mt-3 flex gap-3">
                  <div className="flex-1 bg-white/5 rounded-xl p-2 text-center">
                    <p className="text-gold font-bold text-sm">{routeInfo.distance}</p>
                    <p className="text-snow/50 text-xs">Distance</p>
                  </div>
                  <div className="flex-1 bg-white/5 rounded-xl p-2 text-center">
                    <p className="text-gold font-bold text-sm">{routeInfo.duration}</p>
                    <p className="text-snow/50 text-xs">Est. Time</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Nearby search panel ── */}
          {showNearbyPanel && (
            <div className="absolute bottom-4 left-3 z-20 w-72 bg-navy/95 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl max-h-80 overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-snow font-semibold text-sm flex items-center gap-2">🔍 Nearby Places</h3>
                <button onClick={() => setShowNearbyPanel(false)} className="text-snow/40 hover:text-snow text-lg leading-none">×</button>
              </div>
              {/* Category selector */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {NEARBY_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setNearbyCategory(cat.id)}
                    className={`px-2 py-1 rounded-lg text-xs transition-all ${
                      nearbyCategory === cat.id
                        ? 'bg-gold/20 border border-gold/40 text-gold' :'bg-white/5 border border-white/10 text-snow/60 hover:text-snow'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <button
                onClick={searchNearby}
                disabled={nearbyLoading}
                className="w-full bg-sky/20 border border-sky/30 text-sky font-medium text-sm py-2 rounded-xl hover:bg-sky/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mb-3"
              >
                {nearbyLoading ? <span className="w-4 h-4 border-2 border-sky border-t-transparent rounded-full animate-spin" /> : '🔍'}
                {nearbyLoading ? 'Searching…' : `Search ${NEARBY_CATEGORIES.find(c => c.id === nearbyCategory)?.label || ''}`}
              </button>
              {nearbyPlaces.length > 0 && (
                <div className="space-y-2">
                  {nearbyPlaces.map(place => (
                    <div key={place.id} className="flex items-center gap-2 bg-white/5 rounded-xl p-2">
                      <span className="text-lg">{CATEGORY_ICONS[place.type] || '📍'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-snow text-xs font-medium truncate">{place.name}</p>
                        {place.distance && (
                          <p className="text-snow/40 text-xs">{(place.distance / 1000).toFixed(1)} km away</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!userLocation && (
                <p className="text-snow/40 text-xs text-center mt-2">
                  💡 Detect your location for accurate nearby results
                </p>
              )}
            </div>
          )}

          {/* ── Location error ── */}
          {locError && (
            <div className="absolute bottom-4 right-3 z-20 bg-red-500/20 border border-red-500/30 rounded-xl px-3 py-2 text-red-400 text-xs max-w-xs">
              ⚠️ {locError}
              <button onClick={() => setLocError('')} className="ml-2 text-red-400/60 hover:text-red-400">×</button>
            </div>
          )}

          {/* ── Selected marker card ── */}
          {selectedMarker && !showRoutePanel && !showNearbyPanel && (
            <div className="absolute bottom-4 left-4 right-4 z-10">
              <div className="bg-navy/95 backdrop-blur-md border border-white/10 rounded-2xl p-3 flex items-center gap-3 max-w-sm mx-auto shadow-2xl">
                {selectedMarker.image && (
                  <img src={selectedMarker.image} alt={selectedMarker.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-snow font-semibold text-sm truncate">{selectedMarker.title}</p>
                  {selectedMarker.description && (
                    <p className="text-snow/50 text-xs truncate">{selectedMarker.description}</p>
                  )}
                  {selectedMarker.rating && (
                    <p className="text-gold text-xs">⭐ {selectedMarker.rating}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {selectedMarker.link && (
                    <a href={selectedMarker.link} className="text-xs bg-gold/20 text-gold px-3 py-1.5 rounded-lg hover:bg-gold/30 transition-colors">
                      View
                    </a>
                  )}
                  <button onClick={() => setSelectedMarker(null)} className="text-snow/40 hover:text-snow text-lg leading-none">×</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Helper: add markers to map ───────────────────────────────────────────────

function addMarkersToMap(
  L: any,
  map: any,
  markers: MapMarker[],
  onSelect: (m: MapMarker) => void
) {
  markers.forEach(marker => {
    const icon = L.divIcon({
      html: `<div style="background:#D4AF37;color:#0B2D5C;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);cursor:pointer">${CATEGORY_ICONS[marker.category || 'default'] || '📍'}</div>`,
      className: '',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });

    const m = L.marker([marker.lat, marker.lng], { icon }).addTo(map);
    m.on('click', () => onSelect(marker));
    m.bindTooltip(marker.title, { permanent: false, direction: 'top', offset: [0, -18] });
  });
}
